/**
 * Base Inputs for Rapid Extract fields
 * @module
 */
import * as $ from 'jquery'
import * as _ from 'lodash'
import { VBoolean, VString, Div, Span, all, not, and, if as _if } from 'alkali'
import TextInputControl from 'components/generic/TextInputControl'
import NumberInputControl from 'components/generic/NumberInputControl'
import Icon from 'components/Icon'
import * as editIcon from '../../icons/pencil-write-3.svg'
import * as advancedIcon from '../../icons/pencil-write-2.svg'
import * as confirmIcon from '../../icons/check-button.svg'
import * as ciIcon from 'icons/expand-vertical-4.svg'
import * as errIcon from 'icons/alert-diamond.svg'
import { PLUS_MINUS} from 'util/QuantifierParser'
import UnitOfMeasureInput from 'components/binding/dialogs/quantifier/UnitOfMeasureInput'
import TermInput from '../generic/TermInput'
import PhraseInput from '../generic/PhraseInput'
import { indexedRefdUnits, refdUnitList, dosageUnitIndex, dosageUnitList, timepointUnitIndex, timepointUnitList } from './model/units'
import { validateFn, notEmptyFn, normalize } from './model/validation'
import {
	VBinaryCell,
	binaryCellValidation,
	VCountCell,
	countCellValidation,
	VContinuousCell, emptyUnit, findDirectionality, parseQuantifierVal
} from './model/extraction'
import TextAreaControl from 'components/generic/TextAreaControl'

export const valid = (value, validation = notEmptyFn, optional = false) =>
	all([value, validation]).to(([o, validation]) => {
		const empty = _.isEmpty(normalize(o))
		// if it's optional and empty, it's indeterminate, not affirmatively valid
		if (optional && empty) return false
		return !empty && validation?.isValid
	})

export const invalid = (value, validation = value.to(notEmptyFn), optional = false) =>
	all([value, validation]).to(([o, validation]) => {
		const empty = _.isEmpty(normalize(o))
		// if it's optional and empty, it's indeterminate, not affirmatively invalid
		if (optional && empty) return false
		const errs = validation || []
		const invalid = empty || !validation?.isValid
		return invalid && errs
	})

export const validityClasses = (value, validation, optional = false) => ({
	'valid-field': valid(value, validation, optional),
	'invalid-field': invalid(value, validation, optional)
})


const FIELD_CONTROL_PROPS = {
	created(props) {
		props.class = `${props.className || ''}` + (props.ftype ? ` ${props.ftype} ${props.ftype}-field` : '')
		if (!props.validation) throw new Error('No validation fn for field type: ' + props.ftype)
		props.classes = validityClasses(props.value, props.validation, props.optional)
		props.validField = props.classes['valid-field']
		props.invalidField = props.classes['invalid-field']
		const title = props.title
		props.title = all([props.value, props.invalidField], (value, errs) => {
			if (Array.isArray(errs)) {
				let displayStr = props.placeholder?.valueOf()
				if (displayStr == 'sd' || displayStr == 'se')
					displayStr = displayStr.toUpperCase() 

				let msg = 'Please enter valid ' + (displayStr || 'value') + '.'
				const errMsg = errs.join('\n')
				if (errMsg) msg += '\n' + errMsg
				return msg
			} else {
				return title || value || ''
			}
		})
	}
}

export const FieldControl = TextInputControl.with('.dre-ds-ann', _.clone(FIELD_CONTROL_PROPS))
export const NumberFieldControl = NumberInputControl.with('.dre-ds-ann', _.clone(FIELD_CONTROL_PROPS))
export const TextAreaFieldControl = TextAreaControl.with(_.clone(FIELD_CONTROL_PROPS))

const EditIcon = Icon('.clickable', {
	title: 'Simple',
	icon: editIcon,
	onclick(e) {
		e.stopPropagation()
		e.preventDefault()
		this.advanced.put(!this.advanced.valueOf())
	}
})

const ConfirmIcon = Icon({
	title: 'Confirm',
	icon: confirmIcon,
	onclick(e) {
		e.stopPropagation()
		e.preventDefault()
		if (!this.disabled.valueOf()) {
			this.edit.put(!this.edit.valueOf())
		}
	}
})

class AdvancedIcon extends Icon.with({
	title: 'Advanced',
	icon: advancedIcon,
	onclick(e) {
		e.stopPropagation()
		e.preventDefault()
		if (this.disabled.valueOf()) return
		const adv = !this.advanced.valueOf()
		if (adv && this.onAdvanced) {
			this.onAdvanced()
		}
		this.advanced.put(adv)
	}
}) {
	created(props) {
		props.classes = {
			disabled: props.disabled,
			clickable: not(props.disabled)
		}
		props.disabled || (props.disabled = new VBoolean(false))
		super.created(props)
	}
}

/**
 * A data input widget with validation, unassociated with an extract annotation
 */
export class DataField extends Div.with('.extract-field') {
       protected val
       protected ftype
       created(props) {
               props.val = props.value
               //const isvalid = valid(props.val, ftype, props.optional)
               props.content = this.generateContent(props.val, props.ftype, props.optional, props)
       }
       protected generateContent(val, ftype, optional, props) {
               const cfg = { value: val, validation: props.validation || props.obj?.validation, ftype, optional }
               if (props.placeholder) cfg.placeholder = props.placeholder
               const fieldControl = new FieldControl(cfg)
               return [
                       Div('.extract-simple', [
                               Div('.field-wrap', [fieldControl]),
                       ])
               ]
       }

       setValue(v) {
               this.val.put(v)
       }
}

/**
 * Base extract form field. Binds viewmodel objects and validations to input, and defines
 * a field type that is eligible for assignment by annotation selection.
 */
export class ExtractField extends Div.with('.extract-field', {
	/**
	 * when we receive focus, update the current focusedField
	 */
	onfocusin(e) {
		this.focusedState.focused = this.ftype
		this.focusedState.focusedField = e.currentTarget
		console.log(this.val.valueOf(), this.annot.valueOf())
	}
}) {
	protected val
	protected annot
	protected focusedState
	protected ftype
	private readonly subs?
	created(props) {
		const field = props.field || 'val'
		props.val = props.val || props.obj.property(field)
		props.annot = props.annot || props.obj.property('a')
		props.advanced = props.advanced || props.obj.property('advanced')
		props.focusedState || (props.focusedState = {})
		//const isvalid = valid(props.val, ftype, props.optional)
		props.content = this.generateContent(props.val, props.ftype, props.optional, props, props.advanced)
	}
	protected generateContent(val, ftype, optional, props, advanced) {
		const cfg = { value: val, validation: props.validation || props.obj?.validation, ftype, optional }
		if (props.placeholder) cfg.placeholder = props.placeholder
		if (props.title) cfg.title = props.title
		const fieldControl = new FieldControl(cfg)
		return [
			Div('.extract-simple', [
				Div('.field-wrap', [fieldControl]),
			])
		]
	}

	setAnnotation(a) {
		this.annot.put(a)
		const value = (a.parsed != null && typeof a.parsed == 'number') ? (a.parsed + '') : (a.captured || a.text)
		this.setValue(value)
	}
	setValue(v) {
		this.val.put(v)
	}

	protected detached() {
		this.subs?.forEach(s => s.unsubscribe())
	}

}

/**
 * An extract field that is togglable between a "simple" and "advanced" mode.
 */
abstract class AdvancedExtractField extends ExtractField.with('.advanced') {
	//created(props) {
		//const isvalid = valid(props.val, ftype, props.optional)
		//props.class = ftype.to(type => `${props.className || ''} ${type}`)

		// wait for initialization turns before automatically entering edit mode
		//setTimeout(() => isvalid.subscribe(e => { if (!e.value()) edit.put(true) }), 500)
	//}
	protected generateContent(val, ftype, optional, props, advanced): any[] {
		return [
			...this.simpleContent(props.val, ftype, props.optional, props, advanced),
			...this.advancedContent(props.val, ftype, props.optional, props, advanced),
		]
	}

	protected simpleContent(val, ftype, optional, props, advanced) {
		const cfg = { value: val, validation: props.validation || props.obj?.validation, ftype, optional, disabled: props.disabled, onblur: props.onblur }
		if (props.placeholder) cfg.placeholder = props.placeholder
		const fieldControl = new FieldControl(cfg)
		return [
			Div('.extract-simple', {
				classes: { hidden: advanced }
			}, [
				Div('.field-wrap', [fieldControl]),
				Div('.icon-wrap', [new AdvancedIcon({ advanced, disabled: props.disabled })])
			])
		]
	}

	protected abstract advancedContent(val, ftype, optional, props, advanced)
}

export class BinaryValueField extends ExtractField.with({ ftype: 'binary-value'}) {
	created(props) {
		//props.validation || (props.validation = props.obj.property('val').to(validBinaryValue))
		props.obj = props.obj.as(VBinaryCell)
		props.validation = binaryCellValidation(props.obj, props.unit)
		super.created(props)
	}
	setAnnotation(a) {
		this.annot.put(a)
		let value
		if (a.parsed != null) {
			if (typeof a.parsed == 'number') {
				value = a.parsed
			} else if (a.parsed.quantifier) {
				value = a.parsed.quantifier.High?.Units?.[0]?.Number
			}
		}
		if (value == null) value = a.captured || a.text || ''
		this.setValue((value + '').trim())
	}
}

export class CountValueField extends ExtractField.with({ ftype: 'count-value'}) {
	created(props) {
		//props.validation || (props.validation = props.obj.property('val').to(validBinaryValue))
		props.obj = props.obj.as(VCountCell)
		props.validation = countCellValidation(props.obj.val)
		super.created(props)
	}
	setAnnotation(a) {
		this.annot.put(a)
		let value
		if (a.parsed != null) {
			if (typeof a.parsed == 'number') {
				value = a.parsed
			}
		}
		if (value == null) value = a.captured || a.text || ''
		this.setValue((value + '').trim())
	}
}

export class TermExtractField extends ExtractField {
	private termInput: any
	protected generateContent(val, ftype, optional, props, advanced) {
		const cfg = {
			termType: ftype,
			val: props.obj.val,
			valueVar: props.obj.term,
		}
		if (props.placeholder) cfg.placeholder = props.placeholder
		return [
			Div('.extract-simple', [
				this.termInput = new TermInputField(cfg, props.obj.val)
			])
		]
	}
	//protected editContent(val, ftype, optional, props, valid, edit) {}
	// protected simpleContent(val, ftype, optional, props, valid, advanced) {
	//   return [
	//     Div('.extract-simple', {
	//       hidden: advanced
	//     }, [
	//       Span({
	//         title: props.obj.property('term').to(term => {
	//           if (term) {
	//             return termToString(term)
	//           }
	//           return val
	//         })
	//       }, [val]),
	//       AdvancedIcon({ advanced })
	//     ])
	//   ]
	// }
	setValue(v) {
		this.val.put(v)
		$(this.termInput).typeahead('val', v)
	}
}

export class PhraseExtractField extends ExtractField {
	private phraseInput: any
	private onAssignValue: (v: any) => void
	protected generateContent(val, ftype, optional, props, advanced) {
		const cfg = {
			termType: ftype,
			obj: props.obj,
			val: props.obj.val,
			valueVar: props.obj.term,
			disabled: props.disabled,
			onAssignValue: props.onAssignValue,
			allowFreeText: props.allowFreeText,
			freeTextWarning: props.freeTextWarning
		}
		if (props.placeholder) cfg.placeholder = props.placeholder
		return [
			Div('.extract-simple', [
				this.phraseInput = new PhraseInputField(cfg, props.obj.val)
			])
		]
	}
	//protected editContent(val, ftype, optional, props, valid, edit) {}
	// protected simpleContent(val, ftype, optional, props, valid, advanced) {
	//   return [
	//     Div('.extract-simple', {
	//       hidden: advanced
	//     }, [
	//       Span({
	//         title: props.obj.property('term').to(term => {
	//           if (term) {
	//             return termToString(term)
	//           }
	//           return val
	//         })
	//       }, [val]),
	//       AdvancedIcon({ advanced })
	//     ])
	//   ]
	// }
	setValue(v) {
		this.val.put(v)

		var pi = this.phraseInput

		//attempt to find match
		setTimeout(() => {
			var text = v.toLowerCase().trim()  

			if (pi.source.lastSuggestions) {
				pi.source.lastSuggestions.then(suggestions => {
					//if a match, auto-select it
					var found = suggestions.find(ls => ls.label.toLowerCase() == text);
					
					if (found)
						pi.updateValue(found)
					else	//null out valueVar
						pi.updateValueOnly(null)
					$(pi).typeahead('close')
				})
			}
			else	//null out valueVar
				pi.updateValueOnly(null)
		})

		$(this.phraseInput).typeahead('val', v)
	}
}

export class OutcomeExtractField extends PhraseExtractField {
	created(props) {
		const outcome = props.outcome
		super.created(Object.assign(props, {
			title: all([outcome.name, outcome.type]).to(([o,t]) => o ? `${o.val} (${t})` : ''),
			disabled: not(props.configurable),
			ftype: 'outcome',
			placeholder: 'outcome name',
			obj: outcome.name,
			allowFreeText: true,
			freeTextWarning: 'This outcome does not match an existing record in our ontology. Please double check spelling before proceeding.',
			onAssignValue: term => {
				if (term) {
					const outcomes = _.result([props.allOutcomes], 0) || []
					const dir = findDirectionality(term.ids, outcomes)
					if (dir !== undefined) {
						outcome.property('initIsPositive').put(dir)
						props.isPositive.put(dir)
						outcome.property('isPositive').put(undefined)
					}
				}
			}
		}))
	}
}

export class UnitOfMeasureField extends UnitOfMeasureInput.with('.dre-ds-ann.unit.unit-field', { hasFeedback: false }) {
	created(props) {
		props.unitIndex || (props.unitIndex = indexedRefdUnits())
		props.unitList || (props.unitList = refdUnitList())
		super.created(props)
		props.classes = validityClasses(props.content, props.content.to(v => {
			v = (v || '').trim()
			return !!v && this.source.findExact(v).then(result => !!result)
		}).to(isValid => {
			const result = []
			result.isValid = isValid
			return result
		}))
		//props.title || (props.title = _if(props.classes['invalid-field'], ['Please enter valid unit']))
		if (!props.title) {
			props.title = all([props.valueVar, props.classes['invalid-field']], (item, invalid) =>
				invalid ? ('Please enter valid ' + (props.placeholder || 'value'))
				        : item ? source.itemTitle(item) : `${props.content.valueOf()}`
			)
		}
	}

	setTypeAheadVal(val) {
		$(this).typeahead('val', val)
	}
}

export class TermInputField extends TermInput.with('.dre-ds-ann', { hasFeedback: false }) {
	created(props) {
		props.className += ` ${props.termType} ${props.termType}-field`
		super.created(props)
		props.classes = validityClasses(props.valueVar, props.valueVar.to(validateFn(v =>
			// the field is valid if any valid term object has been assigned
			!!v
		)))
	}
}

export class PhraseInputField extends PhraseInput.with('.dre-ds-ann', { hasFeedback: false }) {
	created(props) {
		const isinvalid = invalid(props.obj, props.obj.validation)
		const isfreetext = props.valueVar.to(v => v && (v?.fulltext || (v.label && !v.senses?.length)))
		props.className += ` ${props.termType} ${props.termType}-field`
		props.classes = {
			'valid-field': valid(props.obj, props.obj.validation),
			'invalid-field': isinvalid,
			'unknown-term': and(not(isinvalid), isfreetext)
		}
		if (props.allowFreeText == null) {
			props.allowFreeText = false
		}
		super.created(props)
	}
}

export class AgeExtractField extends AdvancedExtractField {
	created(props) {
		props.num = props.obj.property('num')
		props.sd = props.obj.property('sd')
		super.created(props)
	}
	protected advancedContent(val, ftype, optional, props, advanced) {
		return [
			Div('.extract-advanced', {
				classes: { hidden: not(advanced) }
			}, [
				FieldControl({ placeholder: 'value', ftype: 'continuous-value', value: props.num, validation: props.num.validation }),
				Span('.plus-minus', PLUS_MINUS),
				FieldControl({ placeholder: 'sd', ftype: 'sd', value: props.sd, validation: props.sd.validation  }),
				EditIcon({ advanced })
			])
		]
	}
}

class QuantifierExtractField extends AdvancedExtractField.with({ hasUOM: true }) {
	private uomField: any
	created(props) {
		props.num = props.obj.property('num')
		props.sd = props.obj.property('sd')
		props.unit = props.obj.property('unit')
		if (props.syncUnit) {
			this.subs = [props.syncUnit.var.subscribe(u => {
				props.unit.put('')
				// u = (u.value() || '').trim()
				// if (u != (props.unit.valueOf() || '').trim()) {
				// 	props.unit.put(u)
				// }
			})]
			props.onblur = () => {
				const u = (props.unit.valueOf() || '').trim()
				if (!emptyUnit(u) && (props.syncUnit.var.valueOf() || '').trim() != u) {
					props.syncUnit.setValue(u)
					props.unit.put('')
				}
			}
		}
		props.devType || (props.devType = new VString())
		if (props.hasSD) props.placeholder = props.devType.to(type => 'value +/- ' + (type || '').toUpperCase())
		super.created(props)
	}
	protected advancedContent(val, ftype, optional, props, advanced) {
		return [
			Div('.extract-advanced', {
				classes: { hidden: not(advanced) }
			}, [
				FieldControl({ placeholder: 'value', ftype: 'continuous-value', value: props.num, validation: props.num.validation }),
				...(props.hasSD ? [
					Span('.plus-minus', PLUS_MINUS),
					FieldControl({ placeholder: props.devType.to(t => t || 'sd'), ftype: 'sd', value: props.sd, validation: props.sd.validation })
				] : []),
				props.hasUOM ? (this.uomField = new UnitOfMeasureField({ unitIndex: props.unitIndex, unitList: props.unitList }, props.unit)) : undefined,
				EditIcon({ advanced }),
				props.hasCI ? Icon({ icon: ciIcon }) : undefined
			])
		]
	}
}

export class ContinuousValueExtractField extends ExtractField.with({ ftype: 'continuous-value' }) {
	created(props) {
		//props.validation || (props.validation = props.obj.to(validateContinuousRow))
		props.obj = props.obj.as(VContinuousCell)
		props.num = props.obj.property('num')
		props.sd = props.obj.property('sd')
		super.created(props)
		this.setParsed(props.obj.val.valueOf(), props.num, props.sd, props.syncUnit,false)
	}

	attached() {
		this.subs = [all([this.num, this.sd, this.syncUnit.var], (num, sd, unit) => {
			sd = (sd || '').trim()
			return (num || '') + (sd ? (' ' + PLUS_MINUS + ' ' + sd) : '') + ((!!unit && !emptyUnit(unit)) ? (' ' + unit) : '')
		}).subscribe(_.after(2, e => {
			if (!this.updating) {
				// wait a turn to see if we're still actually live
				setTimeout(() =>
					document.contains(this) && this.obj.val.put(e.value())
				)
			}
		}))]
	}

	protected generateContent(val, ftype, optional, props) {
		const fieldControl = new FieldControl({ placeholder: 'value', ftype: 'continuous-value', value: props.num, validation: props.num.validation, onpaste: this.onPasteNum.bind(this) })
		return [
			Div('.extract-simple', [
				Div('.field-wrap', [fieldControl])
			])
		]
	}

	onPasteNum(e) {
		console.log(e)
		e.preventDefault()
		e.stopPropagation()
		e.stopImmediatePropagation()
		const text = e.clipboardData?.getData('text')
		if (text && text.trim()) {
			this.setValue(text)
		}
	}

	setValue(v) {
		this.val.put(v)
		this.setParsed(v)
	}

	private setParsed(val, thisNum = this.num, thisSD = this.sd, syncUnit = this.syncUnit, assignUnit = this.assignUnit) {
		this.updating = true
		try {
			const { num, sd, unit } = parseQuantifierVal(val)
			thisNum.put(num)
			thisSD.put(sd)
			if (assignUnit?.valueOf() !== false && !!(unit || '').trim()) {
				syncUnit?.setValue(unit)
			}
		} finally {
			this.updating = false
		}
	}
}

export class ContinuousSDExtractField extends ExtractField.with({ ftype: 'sd', field: 'sd' }) {
	created(props) {
		//props.validation || (props.validation = props.obj.to(validateContinuousRow))
		props.obj = props.obj.as(VContinuousCell)
		props.sd = props.obj.property('sd')
		props.devType || (props.devType = new VString())
		super.created(props)
	}

	protected generateContent(val, ftype, optional, props) {
		const fieldControl = new FieldControl({ placeholder: props.devType.to(t => t || 'sd'), ftype: 'sd', value: props.sd, validation: props.sd.validation })
		return [
			Div('.extract-simple', [
				Div('.icon-wrap', [Span('.plus-minus', PLUS_MINUS)]),
				Div('.field-wrap', [fieldControl])
			])
		]
	}
}

export class DosageExtractField extends QuantifierExtractField {
	created(props) {
		props.unitIndex = dosageUnitIndex()
		props.unitList = dosageUnitList()
		super.created(props)
	}
}

export class TimepointExtractField extends QuantifierExtractField {
	created(props) {
		props.unitIndex = timepointUnitIndex()
		props.unitList = timepointUnitList()
		props.placeholder  || (props.placeholder = 'timepoint')
		super.created(props)
	}
}

// export const AnnotationTextInputControl = Div('.extract-field', {
//   created(props) {
//     const field = props.field || 'value'
//     props.valueVar = props.obj.property(field)
//     props.annot = props.obj.property('a')
//     const ftype = new Variable(props.ftype)
//     //props.class = ftype.to(type => `${props.className || ''} ${type}`)
//     props.content = [
//       FieldControl({
//         value: props.valueVar,
//         ftype,
//         optional: props.optional
//       })
//     ]
//   },
//   onfocusin(e) {
//     this.focusedState.focused = this.ftype
//     this.focusedState.focusedField = e.currentTarget
//     console.log(this.valueVar.valueOf(), this.annot.valueOf())
//   },
//   setAnnotation(a) {
//     this.annot.put(a)
//     this.setValue(a.captured || a.text)
//   },
//   setValue(v) {
//     this.valueVar.put(v)
//   }
// })
